import { apiClient } from './api';

// The standalone CMS bundle has no notification-service; case-management serves the
// same routes and response shapes under its own prefix.
const BASE = import.meta.env.VITE_APP_VARIANT === 'cms' ? '/case/notifications' : '/notifications';

export interface AssetNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  link?: string | null;
  metadata?: Record<string, unknown> | null;
  read: boolean;
  created_at: string;
  read_at?: string | null;
  organization_id?: string | null;
}

export interface AssetNotificationsListResponse {
  notifications: AssetNotification[];
  total: number;
  unreadCount: number;
  page: number;
  pageSize: number;
}

export interface NotificationPreferencesResponse {
  preferences: Array<{
    id: number;
    userSub: string;
    eventType: string;
    channel: string;
    enabled: boolean;
  }>;
}

interface RawNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  link?: string | null;
  metadata?: Record<string, unknown> | null;
  read: boolean;
  createdAt?: string;
  created_at?: string;
  readAt?: string | null;
  read_at?: string | null;
  organizationId?: string | null;
  organization_id?: string | null;
}

function normalizeNotification(row: RawNotification): AssetNotification {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    message: row.message,
    link: row.link ?? null,
    metadata: row.metadata ?? null,
    read: Boolean(row.read),
    created_at: String(row.createdAt ?? row.created_at ?? new Date().toISOString()),
    read_at: row.readAt ?? row.read_at ?? null,
    organization_id: row.organizationId ?? row.organization_id ?? null,
  };
}

export const notificationsService = {
  async list(options: {
    page?: number;
    pageSize?: number;
    unreadOnly?: boolean;
    /** Comma-separated notification type(s), e.g. 'case_assigned,case_status_changed' */
    type?: string;
  } = {}): Promise<AssetNotificationsListResponse> {
    const response = await apiClient.get<{
      notifications: RawNotification[];
      total: number;
      unreadCount: number;
      page: number;
      pageSize: number;
    }>(BASE, { params: options });

    return {
      notifications: (response.notifications ?? []).map(normalizeNotification),
      total: response.total,
      unreadCount: response.unreadCount,
      page: response.page,
      pageSize: response.pageSize,
    };
  },

  async count(): Promise<{ unreadCount: number; total: number }> {
    return apiClient.get<{ unreadCount: number; total: number }>(`${BASE}/count`);
  },

  async markRead(notificationIds: number[]): Promise<{ count: number }> {
    return apiClient.post<{ count: number }>(`${BASE}/mark-read`, {
      notificationIds,
    });
  },

  async markAllRead(): Promise<{ count: number }> {
    return apiClient.post<{ count: number }>(`${BASE}/mark-all-read`, {});
  },

  async markOneRead(id: number): Promise<AssetNotification> {
    const response = await apiClient.patch<RawNotification>(`${BASE}/${id}/read`, {});
    return normalizeNotification(response);
  },

  async remove(id: number): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(`${BASE}/${id}`);
  },

  async clearRead(): Promise<{ count: number }> {
    return apiClient.delete<{ count: number }>(`${BASE}/clear/read`);
  },

  async getPreferences(): Promise<NotificationPreferencesResponse> {
    return apiClient.get<NotificationPreferencesResponse>(`${BASE}/preferences`);
  },

  async getEventTypes(): Promise<string[]> {
    return apiClient.get<string[]>(`${BASE}/preferences/event-types`);
  },

  async initializePreferences(): Promise<NotificationPreferencesResponse> {
    return apiClient.post<NotificationPreferencesResponse>(`${BASE}/preferences/initialize`, {});
  },

  async updatePreference(payload: {
    eventType: string;
    channel: string;
    enabled: boolean;
  }): Promise<{ id: number; eventType: string; channel: string; enabled: boolean }> {
    return apiClient.patch<{ id: number; eventType: string; channel: string; enabled: boolean }>(
      `${BASE}/preferences`,
      payload,
    );
  },

  /**
   * Platform-admin action: notify an organization's members to resolve their
   * open critical/high vulnerabilities. Lives on asset-service (not
   * notification-service) because it needs asset-service's own vulnerability
   * counts to validate/compose the message; asset-service delegates the
   * actual notification creation to notification-service internally.
   */
  async notifyOrganizationVulnerabilities(payload: {
    organizationId: string;
    note?: string;
  }): Promise<{
    organizationId: string;
    recipients: number;
    criticalCount: number;
    highCount: number;
  }> {
    return apiClient.post<{
      organizationId: string;
      recipients: number;
      criticalCount: number;
      highCount: number;
    }>('/asset/vulnerabilities/notify-organization', payload);
  },
};
